import React, { useState } from 'react';
import { Card, Button, Form, Input, message, Space, Typography, Row, Col, Divider } from 'antd';
import { MailOutlined, CheckCircleOutlined, StarOutlined, SecurityScanOutlined } from '@ant-design/icons';
import apiService from '../services/apiService';
import { useAuth } from '../context/AuthContext';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

const ComplianceIntro = () => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      await apiService.submitFeedback(values.email, values.message);
      message.success('Your message has been sent successfully! We will contact you soon.');
      form.resetFields();
    } catch (error) {
      console.error('Error sending feedback:', error);
      message.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <SecurityScanOutlined style={{ fontSize: '24px', color: '#8b5cf6' }} />,
      title: 'Advanced Compliance Analysis',
      description: 'AI-powered analysis of your app data to ensure compliance with various regulations and standards.'
    },
    {
      icon: <CheckCircleOutlined style={{ fontSize: '24px', color: '#10b981' }} />,
      title: 'Automated Scoring',
      description: 'Get comprehensive scorecards and automated evaluations based on your compliance requirements.'
    },
    {
      icon: <StarOutlined style={{ fontSize: '24px', color: '#f59e0b' }} />,
      title: 'Chat Assistant',
      description: 'Interactive AI assistant to help you understand compliance issues and get recommendations.'
    }
  ];

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #0a0a0b 0%, #1a1a1b 50%, #111111 100%)',
      padding: '40px 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Row gutter={[32, 32]} style={{ maxWidth: '1200px', width: '100%' }}>
        {/* Left Column - Features */}
        <Col xs={24} lg={12}>
          <Card
            style={{
              background: '#111111',
              border: '1px solid #262626',
              borderRadius: '12px',
              height: 'fit-content'
            }}
            bodyStyle={{ padding: '32px' }}
          >
            <Title level={2} style={{ color: '#ffffff', marginBottom: '8px' }}>
              Compliance Management
            </Title>
            <Paragraph style={{ color: '#e5e5e5', fontSize: '16px', marginBottom: '32px' }}>
              Unlock powerful compliance analysis and management tools designed for modern app developers.
            </Paragraph>

            <Space direction="vertical" size={24} style={{ width: '100%' }}>
              {features.map((feature, index) => (
                <div key={index} style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <div style={{ flexShrink: 0 }}>
                    {feature.icon}
                  </div>
                  <div>
                    <Title level={4} style={{ color: '#ffffff', margin: '0 0 8px 0', fontSize: '16px' }}>
                      {feature.title}
                    </Title>
                    <Text style={{ color: '#e5e5e5', lineHeight: '1.6' }}>
                      {feature.description}
                    </Text>
                  </div>
                </div>
              ))}
            </Space>

            <Divider style={{ borderColor: '#262626', margin: '32px 0' }} />

            <div style={{ textAlign: 'center' }}>
              <Title level={4} style={{ color: '#ffffff', marginBottom: '8px' }}>
                Ready to get started?
              </Title>
              <Paragraph style={{ color: '#e5e5e5', marginBottom: '0' }}>
                Contact us to unlock access to these powerful compliance tools.
              </Paragraph>
            </div>
          </Card>
        </Col>

        {/* Right Column - Contact Form */}
        <Col xs={24} lg={12}>
          <Card
            style={{
              background: '#111111',
              border: '1px solid #262626',
              borderRadius: '12px'
            }}
            bodyStyle={{ padding: '32px' }}
          >
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <MailOutlined style={{ fontSize: '48px', color: '#8b5cf6', marginBottom: '16px' }} />
              <Title level={3} style={{ color: '#ffffff', marginBottom: '8px' }}>
                Get Access
              </Title>
              <Paragraph style={{ color: '#e5e5e5', marginBottom: '0' }}>
                Send us a message and we'll help you get started with compliance management tools.
              </Paragraph>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{
                email: user?.email || ''
              }}
            >
              <Form.Item
                name="email"
                label={<Text style={{ color: '#ffffff' }}>Email Address</Text>}
                rules={[
                  { required: true, message: 'Please enter your email address' },
                  { type: 'email', message: 'Please enter a valid email address' }
                ]}
              >
                <Input 
                  placeholder="your@email.com"
                  style={{
                    background: '#1a1a1b',
                    border: '1px solid #333333',
                    color: '#ffffff'
                  }}
                />
              </Form.Item>

              <Form.Item
                name="message"
                label={<Text style={{ color: '#ffffff' }}>Message</Text>}
                rules={[
                  { required: true, message: 'Please enter your message' },
                  { min: 10, message: 'Message must be at least 10 characters' }
                ]}
              >
                <TextArea 
                  rows={6}
                  placeholder="Hi, I'm interested in getting access to the compliance management tools. Please let me know how to proceed..."
                  style={{
                    background: '#1a1a1b',
                    border: '1px solid #333333',
                    color: '#ffffff',
                    resize: 'vertical'
                  }}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: '0' }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  size="large"
                  style={{
                    background: '#8b5cf6',
                    borderColor: '#8b5cf6',
                    height: '48px',
                    fontWeight: '500'
                  }}
                >
                  Send Message
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ComplianceIntro;